import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Switch} from '@/web-components';
import {HorizontalFlexWrapper} from '../salesforce/app_salesforce_style';
import {SelectionElementLabel} from '../../../project-tab/projects/project-settings/ProjectSettings_styled';
import DirectApi from '../../../../directApi';
import {createToast} from '../../../shared/components/toasts/toast';
import {
	buildHubspotLinkedFieldsPutBody,
	HUBSPOT_LINKED_FIELD_ORDER_LEGACY,
	HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES,
	normalizeHubspotLinkedFields,
} from './GenericIntegrationLinkedFields';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';

const LinkedFieldsWrapper = styled.div`
	margin: 8px 0;
`;

const bidirectionalKey = field => `${field}Bidirectional`;

const readBidirectionalFromRaw = (raw, field, linkedTrue) => {
	const entry = raw[field];
	if (entry && typeof entry === 'object' && typeof entry.bidirectional === 'boolean') {
		return entry.bidirectional;
	}
	const top = raw[bidirectionalKey(field)];
	if (typeof top === 'boolean') {
		return top;
	}
	if (typeof top === 'string') {
		return top === 'true';
	}
	return linkedTrue;
};

const readBidirectionalFromSyncPayload = (syncRaw, field) => {
	if (!syncRaw || typeof syncRaw !== 'object') {
		return undefined;
	}
	const key = bidirectionalKey(field);
	const top = syncRaw[key];
	if (typeof top === 'boolean') {
		return top;
	}
	if (typeof top === 'string') {
		return top === 'true';
	}
	const entry = syncRaw[field];
	if (entry && typeof entry === 'object' && typeof entry.bidirectional === 'boolean') {
		return entry.bidirectional;
	}
	return undefined;
};

const buildSyncPutBody = bidirectional => {
	const body = {};
	for (const field of HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES) {
		body[bidirectionalKey(field)] = Boolean(bidirectional[field]);
	}
	return body;
};

const fetchPutJson = (endpoint, body) => {
	const headers = new Headers();
	headers.append('Content-Type', 'application/json');
	const init = {
		headers,
		credentials: 'include',
		method: 'PUT',
		body: JSON.stringify(body),
	};
	return fetch(DirectApi.graphqlServerEndpoint(endpoint), init);
};

const persistHubspotSyncDirectionOnly = bidirectional =>
	fetchPutJson('hubspot/sync_direction_settings', buildSyncPutBody(bidirectional));

const showSavedToast = intl =>
	createToast({
		duration: 2000,
		message: intl.formatMessage({id: 'common.changes_saved'}),
	});

const HubspotSyncDirectionSettings = ({linkedFields, onAfterLinkedFieldsPutForDates}) => {
	const intl = useIntl();
	const [bidirectional, setBidirectional] = useState(null);
	const initStartedRef = useRef(false);

	useEffect(() => {
		if (initStartedRef.current) {
			return undefined;
		}
		initStartedRef.current = true;
		let cancelled = false;

		Promise.all([
			DirectApi.Fetch_Raw('hubspot/sync_direction_settings')
				.then(res => (res.ok ? res.json() : {}))
				.catch(() => ({})),
			DirectApi.Fetch('hubspot/linked_fields').then(json => json.linkedFields ?? {}),
		]).then(([syncJson, rawLinked]) => {
			if (cancelled) {
				return;
			}
			const normalizedFromRaw = normalizeHubspotLinkedFields(rawLinked, intl, true);
			const nextBidirectional = {};
			for (const field of HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES) {
				const linkedTrue = normalizedFromRaw[field].linked === true;
				const fromSync = readBidirectionalFromSyncPayload(syncJson, field);
				nextBidirectional[field] =
					fromSync !== undefined ? fromSync : readBidirectionalFromRaw(rawLinked, field, linkedTrue);
			}
			setBidirectional(nextBidirectional);
		});

		return () => {
			cancelled = true;
		};
	}, [intl]);

	useEffect(() => {
		if (!bidirectional) {
			return;
		}
		let changed = false;
		const next = {...bidirectional};
		for (const field of HUBSPOT_LINKED_FIELD_ORDER_LEGACY) {
			if (!linkedFields[field].linked && next[field]) {
				next[field] = false;
				changed = true;
			}
		}
		if (!changed) {
			return;
		}
		setBidirectional(next);
		persistHubspotSyncDirectionOnly(next).catch(() => {});
	}, [linkedFields, bidirectional]);

	const updateSyncDirection = useCallback(
		(field, previousChecked) => {
			if (field !== 'dealDates' && !linkedFields[field].linked) {
				return undefined;
			}
			const nextBidirectional = {...bidirectional, [field]: !previousChecked};

			const defaults = normalizeHubspotLinkedFields({}, intl, true);
			const needsLinkedPersist =
				field === 'dealDates' && nextBidirectional.dealDates === true && linkedFields.dealDates?.linked !== true;

			const mergedLinkedFields = {
				...linkedFields,
				dealDates: {
					...defaults.dealDates,
					...linkedFields.dealDates,
					linked: true,
					text: linkedFields.dealDates?.text ?? defaults.dealDates.text,
				},
			};

			const afterLinkedIfNeeded = () => {
				if (!needsLinkedPersist) {
					return Promise.resolve();
				}
				return fetchPutJson('hubspot/linked_fields', buildHubspotLinkedFieldsPutBody(mergedLinkedFields)).then(() => {
					onAfterLinkedFieldsPutForDates?.();
				});
			};

			return afterLinkedIfNeeded().then(() =>
				persistHubspotSyncDirectionOnly(nextBidirectional).then(() => {
					setBidirectional(nextBidirectional);
					showSavedToast(intl);
				})
			);
		},
		[bidirectional, linkedFields, intl, onAfterLinkedFieldsPutForDates]
	);

	if (!bidirectional) {
		return <InlineLoader />;
	}

	return (
		<>
			{HUBSPOT_LINKED_FIELD_ORDER_WITH_DATES.map(field => (
				<LinkedFieldsWrapper key={`sync-${field}`}>
					<HorizontalFlexWrapper>
						<Switch
							checked={bidirectional[field]}
							locked={field === 'dealDates' ? false : !linkedFields[field].linked}
							onChange={previousChecked => updateSyncDirection(field, previousChecked)}
							cy={`hubspot-sync-direction-${field}`}
						/>
						<SelectionElementLabel>{linkedFields[field].text}</SelectionElementLabel>
					</HorizontalFlexWrapper>
				</LinkedFieldsWrapper>
			))}
		</>
	);
};

export default HubspotSyncDirectionSettings;
